import { Calendar, CalendarX, ShieldCheck, UserCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatEventDate, formatRelative, isPast } from "@/lib/dates";
import type { RecentEvent } from "@/lib/storage";
import styles from "./EventCard.module.css";

type Props = {
  entry: RecentEvent;
  canceled?: boolean;
};

function linkFor(entry: RecentEvent): string {
  return entry.role === "admin"
    ? `/proadmin/${entry.shortcut}/${entry.key}`
    : `/view/${entry.shortcut}/${entry.key}`;
}

export function EventCard({ entry, canceled = false }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "de";
  const past = isPast(entry.eventAt);

  const classes = [
    styles.card,
    canceled ? styles.cardCanceled : undefined,
    !canceled && past ? styles.cardPast : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Link to={linkFor(entry)} className={classes}>
      <div className={styles.topRow}>
        <h3 className={styles.title}>{entry.name}</h3>
        <div className={styles.chips}>
          {canceled && (
            <span className={`${styles.chip} ${styles.chipCanceled}`}>
              <CalendarX size={14} weight="regular" />
              {t("event.canceled")}
            </span>
          )}
          <span className={styles.chip}>
            {entry.role === "admin" ? (
              <>
                <ShieldCheck size={14} weight="regular" />
                {t("event.roleAdmin")}
              </>
            ) : (
              <>
                <UserCircle size={14} weight="regular" />
                {t("event.roleUser")}
              </>
            )}
          </span>
        </div>
      </div>
      <div className={styles.meta}>
        <span className={styles.metaItem}>
          <Calendar size={14} weight="regular" />
          {formatEventDate(entry.eventAt, undefined, locale)}
        </span>
        <span className={`${styles.metaItem} ${styles.countdown}`}>
          {formatRelative(entry.eventAt, locale)}
        </span>
      </div>
    </Link>
  );
}
